import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { SWAL_SETTINGS } from '../../../../../../utils/Constants';
import { staffList } from '../../../../services/auth.services';
import { PaymentMethodList } from '../../../../services/table.services';
import { settleBulkSettlementTable } from './bulkSettlement.services';
import { formatBusinessDate } from './time';
import { buildBulkSettlementTransactionsPayload } from './utils/transactionPayload';
import { useBulkSettlementTableSelection } from './hooks/useBulkSettlementTableSelection';
import { useBulkSettlementTransactionCards } from './transaction-editor/useBulkSettlementTransactionCards';
import TransactionCard from './components/TransactionCard';
import type { BulkSettlementOption, BulkSettlementTransactionCardItem, PaymentMethodItem } from './types';

const CASH_OPTION: BulkSettlementOption = {
  label: 'Cash',
  value: 'cash',
  paymentMethod: 'cash',
  paymentMethodId: null,
  methodName: null,
};

const BulkSettlement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const businessDateFromState: string | undefined = (location.state as any)?.businessDate;
  const businessDate = businessDateFromState || '';

  // No businessDate in router state (direct URL entry, bookmark, or a refresh that
  // dropped history state) — never silently default to today, send the user back
  // to pick a date instead.
  useEffect(() => {
    if (!businessDateFromState) {
      navigate('/cashier/settings/bulk-settlement', { replace: true });
    }
  }, [businessDateFromState, navigate]);

  const [submitting, setSubmitting] = useState(false);
  const [cashierOptions, setCashierOptions] = useState<BulkSettlementOption[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<BulkSettlementOption[]>([
    CASH_OPTION,
  ]);

  const restSettings = useSelector((state: any) => state?.restSettings?.data);
  const currentCashierId: string =
    useSelector((state: any) => state?.cashierDetails?.data?._id) || '';
  const serviceChargePercentage: number = restSettings?.is_service_charge_enabled
    ? Number(restSettings?.service_charge) || 0
    : 0;

  const { resolvedTableCapacity, selectedTableId, tableOptions, updateSelectedTable } =
    useBulkSettlementTableSelection();

  const {
    addTransaction,
    allCardsHavePositiveTotal,
    cards,
    changeCard,
    changePaymentMethod,
    deleteTransaction,
    resetTransactions,
    restoreCards,
    syncDefaultCardValues,
    totalAmount,
  } = useBulkSettlementTransactionCards({
    currentCashierId,
    resolvedTableCapacity,
    serviceChargePercentage,
  });

  useEffect(() => {
    const s = (location.state as any) || {};
    if (s.restoredCards) {
      restoreCards(s.restoredCards as BulkSettlementTransactionCardItem[]);
      window.history.replaceState({ businessDate }, '');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    staffList()
      .then((response: any) => {
        if (!isMounted) return;
        const list: any[] = response?.data || [];
        setCashierOptions(
          list.map((staff: any) => ({ label: staff?.name || '', value: staff?._id || '' })),
        );
      })
      .catch(console.error);
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    Promise.allSettled([
      PaymentMethodList({ global_search: 'qr_payments' }),
      PaymentMethodList({ global_search: 'credit_card' }),
    ]).then(([otherResult, creditResult]) => {
      if (!isMounted) return;
      const otherList: PaymentMethodItem[] =
        otherResult.status === 'fulfilled' ? otherResult.value?.data?.list || [] : [];
      const creditList: PaymentMethodItem[] =
        creditResult.status === 'fulfilled' ? creditResult.value?.data?.list || [] : [];

      const seen = new Set<string>();
      const unique = (opts: BulkSettlementOption[]) =>
        opts.filter(o => {
          const key = (o.methodName || o.label || '').toLowerCase().trim();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      setPaymentMethodOptions([
        CASH_OPTION,
        ...unique([
          ...otherList.map((item): BulkSettlementOption => ({
            label: item.payment_type || '',
            value: `other:${item._id}`,
            paymentMethod: 'other',
            paymentMethodId: item._id,
            methodName: item.payment_type || null,
          })),
          ...creditList.map((item): BulkSettlementOption => ({
            label: item.payment_type || '',
            value: `card:${item._id}`,
            paymentMethod: 'card',
            paymentMethodId: item._id,
            methodName: item.payment_type || null,
          })),
        ]),
      ]);
    });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!currentCashierId && resolvedTableCapacity <= 0) return;
    syncDefaultCardValues({
      cashierId: currentCashierId,
      peopleCount: resolvedTableCapacity > 0 ? String(resolvedTableCapacity) : '',
    });
  }, [currentCashierId, resolvedTableCapacity, syncDefaultCardValues]);

  const peopleCountOptions = useMemo<BulkSettlementOption[]>(() => {
    if (resolvedTableCapacity <= 0) return [];
    return Array.from({ length: resolvedTableCapacity }, (_, i) => ({
      label: String(i + 1),
      value: String(i + 1),
    }));
  }, [resolvedTableCapacity]);

  const canDiscard =
    cards.length > 1 ||
    cards.some(
      card =>
        (card.total !== '' && Number(card.total) > 0) ||
        card.selectedItems.length > 0 ||
        card.paymentMethod !== 'cash',
    );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      canDiscard &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.endsWith('bulk-settlement/menu-items'),
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    Swal.fire({
      title: t('msg_are_you_sure'),
      text: t('label_bulk_settlement_discard_confirm'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6c757d',
      cancelButtonColor: '#dc3545',
      confirmButtonText: t('btn_yes'),
      cancelButtonText: t('btn_cancel'),
      customClass: {
        popup: 'swal-restaurant',
        confirmButton: 'btn btn-secondary col',
        cancelButton: 'btn btn-danger col',
      },
    }).then(result => {
      if (result.dismiss === Swal.DismissReason.close) return;
      try {
        if (result.isConfirmed) blocker.proceed();
        else blocker.reset();
      } catch (_) {}
    });
  }, [blocker.state]);

  useEffect(() => {
    if (!canDiscard) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [canDiscard]);

  const handleChangePaymentMethod = useCallback(
    (cardId: string, option: BulkSettlementOption) => {
      changePaymentMethod(
        cardId,
        option.paymentMethod || 'cash',
        option.paymentMethodId || '',
        option.methodName || null,
      );
    },
    [changePaymentMethod],
  );

  const handleChangeTable = (tableId: string) => {
    if (tableId === selectedTableId) return;
    const doChange = () => {
      const nextTable = updateSelectedTable(tableId);
      resetTransactions({
        cashierId: currentCashierId,
        tableCapacity: Number(nextTable?.capacity) || 0,
      });
    };
    if (canDiscard) {
      Swal.fire({
        title: t('msg_are_you_sure'),
        text: t('label_bulk_settlement_table_change'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#6c757d',
        cancelButtonColor: '#dc3545',
        confirmButtonText: t('btn_yes'),
        cancelButtonText: t('btn_cancel'),
        customClass: {
          popup: 'swal-restaurant',
          confirmButton: 'btn btn-secondary col',
          cancelButton: 'btn btn-danger col',
        },
      }).then(result => { if (result.isConfirmed) doChange(); });
    } else {
      doChange();
    }
  };

  const handleDiscard = () => {
    Swal.fire({
      title: t('msg_are_you_sure'),
      text: t('label_bulk_settlement_discard_confirm'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6c757d',
      cancelButtonColor: '#dc3545',
      confirmButtonText: t('btn_yes'),
      cancelButtonText: t('btn_cancel'),
      customClass: {
        popup: 'swal-restaurant',
        confirmButton: 'btn btn-secondary col',
        cancelButton: 'btn btn-danger col',
      },
    }).then(result => {
      if (result.isConfirmed) {
        resetTransactions({ cashierId: currentCashierId, tableCapacity: resolvedTableCapacity });
      }
    });
  };

  const doConfirm = async () => {
    if (!selectedTableId || !businessDate || !allCardsHavePositiveTotal || submitting) return;
    const now = new Date();
    const hasFutureTime = cards.some(card => {
      const payload = buildBulkSettlementTransactionsPayload({ businessDate, cards: [card], tableId: selectedTableId });
      return new Date(payload.transactions[0].sale_time) > now;
    });
    if (hasFutureTime) {
      Swal.fire({ icon: 'error', text: t('label_bulk_settlement_future_time_error'), ...SWAL_SETTINGS } as any);
      return;
    }
    setSubmitting(true);
    try {
      await settleBulkSettlementTable(
        buildBulkSettlementTransactionsPayload({ businessDate, cards, tableId: selectedTableId }),
      );
      Swal.fire({ icon: 'success', text: t('label_bulk_settlement_success'), ...SWAL_SETTINGS } as any);
      resetTransactions({ cashierId: currentCashierId, tableCapacity: resolvedTableCapacity });
    } catch (err: any) {
      // The service throws with the server's own error code (bulkSettlement.
      // services.ts). Flattening every failure into one sentence made a rejected
      // addon, a missing menu item and an underpayment indistinguishable — from
      // the cashier's side AND from a bug report. Translate the code when a
      // string exists for it; otherwise show the code itself, which at least
      // names the cause. `t` returns the key unchanged when it has no entry.
      const code = typeof err?.message === 'string' ? err.message.trim() : '';
      const translated = code ? t(code) : '';
      const msg =
        code === 'sale_time_future'
          ? t('label_bulk_settlement_future_time_error')
          : (translated && translated !== code ? translated : code) ||
            t('label_bulk_settlement_error');
      Swal.fire({ icon: 'error', text: msg, ...SWAL_SETTINGS } as any);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    Swal.fire({
      title: t('msg_are_you_sure'),
      text: t('btn_confirm_settlement'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6c757d',
      cancelButtonColor: '#dc3545',
      confirmButtonText: t('btn_yes'),
      cancelButtonText: t('btn_cancel'),
      customClass: {
        popup: 'swal-restaurant',
        confirmButton: 'btn btn-secondary col',
        cancelButton: 'btn btn-danger col',
      },
    }).then(result => { if (result.isConfirmed) doConfirm(); });
  };

  const handleOpenMenuSelector = (cardId: string) => {
    const card = cards.find(c => c.id === cardId);
    navigate('/cashier/settings/bulk-settlement/menu-items', {
      state: {
        businessDate,
        cardId,
        cards,
        selectedItems: card?.selectedItems || [],
        serviceChargePercentage,
      },
    });
  };

  const canConfirm = allCardsHavePositiveTotal && !!selectedTableId && !submitting;

  if (!businessDateFromState) return null;

  return (
    <div className="cashier-heading-innner">
      <div className="card-outer-header position-relative d-flex justify-content-center align-items-center mb-4">
        <h4 className="heading-title-text mb-0">
          {t('label_bulk_settlement')} : {businessDate}
        </h4>
        <div className="position-absolute end-0 d-flex align-items-center gap-2">
          <span className="fw-medium text-nowrap">{t('label_table_no')}</span>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '140px' }}
            value={selectedTableId}
            onChange={e => handleChangeTable(e.target.value)}
          >
            {tableOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="dashboard-content-wraper" style={{ paddingBottom: '100px' }}>
        <div className="card mb-3">
          <div className="card-body">
            {cards.map(card => (
              <TransactionCard
                key={card.id}
                card={card}
                canDelete={cards.length > 1}
                isToday={businessDate === formatBusinessDate(new Date())}
                cashierOptions={cashierOptions}
                paymentMethodOptions={paymentMethodOptions}
                peopleCountOptions={peopleCountOptions}
                onChangeCard={changeCard}
                onChangePaymentMethod={handleChangePaymentMethod}
                onDeleteCard={deleteTransaction}
                onOpenMenuSelector={handleOpenMenuSelector}
              />
            ))}

            <div className="text-center mt-2">
              <button
                type="button"
                className="btn text-white px-4"
                style={{ backgroundColor: '#ff9500', borderColor: '#ff9500' }}
                disabled={!allCardsHavePositiveTotal || submitting}
                onClick={addTransaction}
              >
                + {t('label_bulk_settlement_add_transaction')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="card"
        style={{ position: 'fixed', bottom: '20px', left: '275px', right: '25px', zIndex: 10, borderRadius: '6px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="card-body d-flex justify-content-between align-items-center py-2">
          <div>
            <div className="text-muted small">{t('label_total')}</div>
            <div className="fs-5 fw-bold">{totalAmount.toFixed(2)}</div>
          </div>
          <div className="d-flex gap-3">
            <button
              type="button"
              className={!canDiscard || submitting ? 'btn' : 'btn btn-danger'}
              style={
                !canDiscard || submitting
                  ? { backgroundColor: '#f0f0f0', borderColor: '#dee2e6', color: '#adb5bd' }
                  : {}
              }
              disabled={!canDiscard || submitting}
              onClick={handleDiscard}
            >
              {t('label_bulk_settlement_discard')}
            </button>
            <button
              type="button"
              className="btn text-white"
              style={{ backgroundColor: '#ff9500', borderColor: '#ff9500' }}
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              {submitting ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : null}
              {t('label_confirm')}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BulkSettlement;
